import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  directionForLocale,
  verificationAllowsDownload,
  type PublicVerificationView,
} from '@lims/shared-types';
import {
  publicVerificationApi,
  type PublicVerificationDownloadResult,
  type PublicVerificationResult,
  type VerificationDownloadRefusal,
  type VerificationUnavailableReason,
} from '../../api/endpoints.js';
import { LanguageSwitcher } from '../../components/LanguageSwitcher.js';
import {
  ActionButton,
  Alert,
  LoadingState,
  Panel,
  StatusBadge,
  toneForStatus,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { saveBlob } from '../reports/report-parts.js';
import {
  formatIssuedAt,
  formatWait,
  pickWithLanguage,
  verificationFileName,
} from './verification-format.js';

/**
 * The PUBLIC report verification page, `/verify/:token` (Phase 10, contract
 * section 6; D111, D115, D120, D122, D123, D130).
 *
 * ===========================================================================
 * THE ONLY PAGE IN THE PRODUCT A STRANGER CAN OPEN. READ CLAUDE.md SECTIONS 5
 * AND 8 BEFORE CHANGING IT.
 * ===========================================================================
 *
 *  - OUTSIDE sign-in and outside the shell, with its own minimal layout. NOT
 *    `PublicShell`: that names the INSTALLATION's laboratory, and several
 *    laboratories share one installation. The laboratory named here comes from
 *    the token's own answer, and from nothing else (D130).
 *  - It shows only what the allow-list carries, and the API client copies only
 *    those keys, so nothing confidential reaches this screen by accident.
 *  - A VALID QR IS NOT SCIENTIFIC CORRECTNESS, AND APPROVAL IS NOT A PASS. Both
 *    sentences are on the page in every state, including while it loads.
 *  - "UNAVAILABLE" IS ABOUT THIS PAGE, NEVER ABOUT THE REPORT. A network
 *    failure, a server error or a rate limit says so, and says it does not
 *    make the report invalid.
 *  - Whether the original is available is stated, never WHY: a payment hold and
 *    a laboratory that does not offer downloads read identically (D111, D115).
 *  - A superseded link describes ITS OWN version, and says so. The newer
 *    version's number and status are shown as exactly what they are; nothing
 *    here calls the newer version valid unless its own status says it.
 */

export function VerificationPage() {
  const { token = '' } = useParams();
  // Keyed by the token, so nothing from one link's answer or download survives
  // into another's.
  return <VerificationView key={token} token={token} />;
}

function VerificationView({ token }: { readonly token: string }) {
  const { t } = useTranslation();
  const whyUnavailable = useWhyUnavailable();

  const verification = useQuery({
    queryKey: ['public-verification', token],
    queryFn: () => publicVerificationApi.get(token),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Offline must still END in "unavailable", rather than wait silently for a
    // connection that may never come.
    networkMode: 'always',
  });

  // The client never throws by design; if it ever did, that is still only
  // "no answer", never a statement about the report.
  const result: PublicVerificationResult | null =
    verification.data ??
    (verification.isError
      ? { kind: 'unavailable', reason: 'unexpected', retryAfterSeconds: null }
      : null);

  const recheck = () => {
    void verification.refetch();
  };

  let content: ReactNode;
  if (result === null) {
    content = <LoadingState label={t('verification.checking')} />;
  } else if (result.kind === 'found') {
    content = (
      <FoundReport
        // A fresh answer starts clean, including any message about a download.
        key={verification.dataUpdatedAt}
        view={result.view}
        token={token}
        onRecheck={recheck}
        isRechecking={verification.isFetching}
      />
    );
  } else if (result.kind === 'not_found') {
    content = (
      <Alert
        tone="warning"
        title={t('verification.heading.not_found')}
        description={t('verification.not_found')}
      />
    );
  } else {
    content = (
      <Alert
        tone="info"
        title={t('verification.heading.unavailable')}
        description={[
          t('verification.unavailable'),
          ...whyUnavailable(result.reason, result.retryAfterSeconds),
        ].join(' ')}
        action={
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={recheck}
            isLoading={verification.isFetching}
          >
            {t('verification.retry')}
          </ActionButton>
        }
      />
    );
  }

  return <VerificationLayout>{content}</VerificationLayout>;
}

/**
 * The page's own frame: the language switch, the title, the state, and — in
 * every state — what this page does and does not confirm.
 */
function VerificationLayout({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (
    <div
      data-testid="verification-page"
      dir={directionForLocale(locale)}
      lang={locale}
      className="flex min-h-screen flex-col bg-default-50"
    >
      {/* `justify-end` is the trailing edge: top right in English, top left in Arabic. */}
      <div className="flex justify-end px-4 pt-4 sm:px-6">
        <LanguageSwitcher />
      </div>

      <main id="main" className="flex flex-1 justify-center px-4 py-8 sm:px-6">
        <div className="flex w-full min-w-0 max-w-2xl flex-col gap-gutter">
          <h1 className="text-title font-semibold text-default-900">
            {t('verification.title')}
          </h1>

          {children}

          <Panel title={t('verification.aboutTitle')}>
            <div
              data-testid="verification-disclaimer"
              className="flex flex-col gap-inline text-body text-default-700"
            >
              <p className="font-medium text-default-800">{t('verification.disclaimer')}</p>
              <p>{t('verification.approvalNote')}</p>
              <p>{t('verification.privacyNote')}</p>
              <p>{t('verification.contactLaboratory')}</p>
            </div>
          </Panel>
        </div>
      </main>
    </div>
  );
}

/** Why no answer came, and how long to wait. Never a claim about the report. */
function useWhyUnavailable() {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (reason: VerificationUnavailableReason, retryAfterSeconds: number | null): string[] => {
    const hint =
      reason === 'rate_limited'
        ? t('verification.rateLimitedHint')
        : reason === 'network'
          ? t('verification.unavailableNetworkHint')
          : t('verification.unavailableServerHint');
    const wait =
      retryAfterSeconds !== null && retryAfterSeconds > 0
        ? [t('verification.retryAfter', { duration: formatWait(retryAfterSeconds, locale) })]
        : [];
    return [hint, ...wait];
  };
}

function Fact({
  label,
  wide = false,
  children,
}: {
  /** Already translated. */
  readonly label: string;
  readonly wide?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className={wide ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
      {/* The 600 step: the lightest ink that reads on white (5.16:1). The 500
          step is decorative only and measured 2.58:1 here in a real browser. */}
      <dt className="text-caption text-default-600">{label}</dt>
      <dd className="mt-1 break-words text-body text-default-800">{children}</dd>
    </div>
  );
}

function FoundReport({
  view,
  token,
  onRecheck,
  isRechecking,
}: {
  readonly view: PublicVerificationView;
  readonly token: string;
  readonly onRecheck: () => void;
  readonly isRechecking: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  const statement = (() => {
    switch (view.status) {
      case 'issued_valid':
        return t('verification.issued_valid');
      // Never "the latest valid version is provided": the facts below are this
      // link's own version, and a file may not be offered at all.
      case 'superseded':
        return t('verification.supersededStatement', { version: view.versionNumber });
      case 'cancelled':
        return t('verification.cancelled');
      case 'suspended':
        return t('verification.suspended');
    }
  })();

  const laboratory = pickWithLanguage(view.laboratoryName, locale);
  const issuedAt = formatIssuedAt(view.issuedAt, locale);
  const titles = view.testTitles.map((title) => pickWithLanguage(title, locale));
  const statusAllowsFile = verificationAllowsDownload(view.status);
  const offersOriginal = view.originalAvailable && statusAllowsFile;

  return (
    <>
      <Alert
        tone={toneForStatus(view.status)}
        title={t(`verification.heading.${view.status}`)}
        description={statement}
      />

      {view.status === 'superseded' && view.latestVersion ? (
        <Panel title={t('verification.newerVersionTitle')}>
          <dl data-testid="verification-newer-version" className="grid grid-cols-1 gap-stack sm:grid-cols-2">
            <Fact label={t('verification.version')}>
              <bdi>{view.latestVersion.versionNumber}</bdi>
            </Fact>
            <Fact label={t('verification.status')}>
              <StatusBadge
                label={t(`status.reportVersion.${view.latestVersion.status}`)}
                tone={toneForStatus(view.latestVersion.status)}
              />
            </Fact>
          </dl>
        </Panel>
      ) : null}

      <Panel title={t('verification.detailsTitle')}>
        <dl data-testid="verification-details" className="grid grid-cols-1 gap-stack sm:grid-cols-2">
          <Fact label={t('verification.laboratoryName')} wide>
            {laboratory ? (
              <bdi lang={laboratory.lang}>{laboratory.text}</bdi>
            ) : (
              t('common.notRecorded')
            )}
          </Fact>
          <Fact label={t('verification.reportNumber')}>
            {/* A machine identifier reads left to right in both languages. */}
            <bdi dir="ltr" className="font-mono">
              {view.reportNumber}
            </bdi>
          </Fact>
          <Fact label={t('verification.version')}>
            <bdi>{view.versionNumber}</bdi>
          </Fact>
          <Fact label={t('verification.status')}>
            <StatusBadge
              label={t(`status.reportVersion.${view.status}`)}
              tone={toneForStatus(view.status)}
            />
          </Fact>
          <Fact label={t('verification.issueDate')}>
            {/* The reader's own zone, named. Isolated, and never forced left to
                right: an Arabic date stays an Arabic date. */}
            <bdi data-testid="verification-issued-at">
              {issuedAt ?? t('common.notRecorded')}
            </bdi>
          </Fact>
          <Fact label={t('verification.pageCount')}>
            <bdi>{view.pageCount}</bdi>
          </Fact>
          <Fact label={t('verification.language')}>{t(`reportLanguage.${view.language}`)}</Fact>
          <Fact label={t('verification.testTitles')} wide>
            {titles.length === 0 ? (
              t('common.notRecorded')
            ) : (
              <ul className="flex flex-col gap-1">
                {titles.map((title, index) => (
                  <li key={index}>
                    {title ? <bdi lang={title.lang}>{title.text}</bdi> : t('common.notRecorded')}
                  </li>
                ))}
              </ul>
            )}
          </Fact>
        </dl>
      </Panel>

      <Panel title={t('verification.originalTitle')}>
        {offersOriginal ? (
          <OriginalDownload
            view={view}
            token={token}
            onRecheck={onRecheck}
            isRechecking={isRechecking}
          />
        ) : (
          <div className="flex flex-col gap-inline text-body">
            <p className="font-medium text-default-800">{t('verification.originalNotAvailable')}</p>
            {/* No reason, ever. For a cancelled or suspended report the status
                above already says what matters. */}
            {statusAllowsFile ? (
              <p className="text-default-600">{t('verification.originalNotAvailableHint')}</p>
            ) : null}
          </div>
        )}
      </Panel>
    </>
  );
}

type DownloadState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'working' }
  | { readonly phase: 'received'; readonly versionNumber: number | null }
  | { readonly phase: 'refused'; readonly refusal: VerificationDownloadRefusal }
  | {
      readonly phase: 'unavailable';
      readonly reason: VerificationUnavailableReason;
      readonly retryAfterSeconds: number | null;
    };

/**
 * The original, fetched and saved without leaving the page, so a refusal is a
 * translated sentence here and never the API's JSON in a bare tab.
 */
function OriginalDownload({
  view,
  token,
  onRecheck,
  isRechecking,
}: {
  readonly view: PublicVerificationView;
  readonly token: string;
  readonly onRecheck: () => void;
  readonly isRechecking: boolean;
}) {
  const { t } = useTranslation();
  const whyUnavailable = useWhyUnavailable();
  const [state, setState] = useState<DownloadState>({ phase: 'idle' });

  const download = async () => {
    setState({ phase: 'working' });
    let result: PublicVerificationDownloadResult;
    try {
      result = await publicVerificationApi.download(token);
    } catch {
      result = { kind: 'unavailable', reason: 'unexpected', retryAfterSeconds: null };
    }

    if (result.kind === 'file') {
      // A superseded link is served the latest valid version, which the API
      // names in a header; a valid link is served its own version.
      const versionNumber =
        result.servedVersionNumber ??
        (view.status === 'issued_valid' ? view.versionNumber : null);
      saveBlob(result.blob, verificationFileName(view.reportNumber, versionNumber));
      setState({ phase: 'received', versionNumber });
    } else if (result.kind === 'refused') {
      setState({ phase: 'refused', refusal: result.refusal });
    } else {
      setState({
        phase: 'unavailable',
        reason: result.reason,
        retryAfterSeconds: result.retryAfterSeconds,
      });
    }
  };

  const refusalSentence = (refusal: VerificationDownloadRefusal): string => {
    switch (refusal) {
      case 'ORIGINAL_NOT_AVAILABLE':
        return t('verification.originalNotAvailable');
      case 'REPORT_CANCELLED':
        return t('verification.cancelled');
      case 'REPORT_SUSPENDED':
        return t('verification.suspended');
      case 'NOT_FOUND':
        return t('verification.not_found');
    }
  };

  return (
    <div className="flex flex-col gap-stack text-body">
      <div className="flex flex-col gap-inline">
        <p className="font-medium text-default-800">{t('verification.originalAvailable')}</p>
        <p className="text-default-600">{t('verification.downloadHint')}</p>
        {view.status === 'superseded' ? (
          <p className="text-default-600">{t('verification.supersededDownloadHint')}</p>
        ) : null}
      </div>

      <div>
        <ActionButton
          priority="primary"
          onPress={() => void download()}
          isLoading={state.phase === 'working'}
        >
          {t('verification.download')}
        </ActionButton>
      </div>

      {state.phase === 'working' ? (
        <p role="status" className="text-default-600">
          {t('verification.downloading')}
        </p>
      ) : null}

      {state.phase === 'received' && state.versionNumber !== null ? (
        <Alert
          tone="info"
          title={t('verification.downloadReceived', { version: state.versionNumber })}
        />
      ) : null}

      {state.phase === 'refused' ? (
        <Alert
          tone="warning"
          title={t('verification.downloadRefusedTitle')}
          description={refusalSentence(state.refusal)}
          action={
            <ActionButton
              priority="secondary"
              size="sm"
              onPress={onRecheck}
              isLoading={isRechecking}
            >
              {t('verification.retry')}
            </ActionButton>
          }
        />
      ) : null}

      {state.phase === 'unavailable' ? (
        <Alert
          tone="warning"
          title={t('verification.downloadUnavailable')}
          description={whyUnavailable(state.reason, state.retryAfterSeconds).join(' ')}
        />
      ) : null}
    </div>
  );
}
